import { useEffect } from "react";
import { useSnapshot } from "valtio";
import { organizationUsersStore, resetOrganizationUsersStore } from "../organizationUsersStore";
import { PermissionService } from "@services/permissions/permissionService";
import showAlert from "src/shared/utils/generic.tsx";

const permissionService = new PermissionService();

const useOrganizationUsers = (organizationId: number) => {
  const snap = useSnapshot(organizationUsersStore);

  useEffect(() => {
    if (!organizationId) return;
    if (organizationId === organizationUsersStore.organizationId) return;
    resetOrganizationUsersStore();
    organizationUsersStore.organizationId = organizationId;
    loadUsers(organizationId);
    loadRoles(organizationId);
  }, [organizationId]);

  async function loadUsers(orgId: number) {
    organizationUsersStore.isLoading = true;
    organizationUsersStore.error = null;
    try {
      organizationUsersStore.users = await permissionService.listOrgUsers(orgId);
    } catch {
      organizationUsersStore.error = "Failed to load users.";
    } finally {
      organizationUsersStore.isLoading = false;
      organizationUsersStore.hasLoaded = true;
    }
  }

  // Scoped to the organization: a client offers only the four customer roles,
  // Motimatic offers all of them (MPN-1606).
  async function loadRoles(orgId: number) {
    try {
      organizationUsersStore.roles = await permissionService.listRoles(orgId);
    } catch {
      console.error("Failed to load roles");
    }
  }

  async function addUser(profileId: number) {
    organizationUsersStore.isSaving = true;
    try {
      await permissionService.addUserToOrg(profileId, organizationId);
      showAlert("User added successfully", "success", 3000);
      await loadUsers(organizationId);
    } catch {
      showAlert("Failed to add user", "error", 3000);
    } finally {
      organizationUsersStore.isSaving = false;
    }
  }

  // Same endpoint as addUser. Linking is idempotent server-side and stamps the
  // org onto the employee record, which is exactly what a mismatched row needs.
  async function repairOrg(profileId: number) {
    organizationUsersStore.isSaving = true;
    try {
      await permissionService.addUserToOrg(profileId, organizationId);
      showAlert("Organization assignment repaired", "success", 3000);
      await loadUsers(organizationId);
    } catch {
      showAlert("Failed to repair organization assignment", "error", 3000);
    } finally {
      organizationUsersStore.isSaving = false;
    }
  }

  async function removeUser(profileId: number) {
    organizationUsersStore.isSaving = true;
    try {
      await permissionService.removeUserFromOrg(profileId, organizationId);
      showAlert("User removed successfully", "success", 3000);
      await loadUsers(organizationId);
    } catch {
      showAlert("Failed to remove user", "error", 3000);
    } finally {
      organizationUsersStore.isSaving = false;
    }
  }

  // Reactivation only. The endpoint takes both directions, but the tab offers
  // no way to switch an account off, so nothing here sends false.
  async function activateUser(profileId: number) {
    organizationUsersStore.isSaving = true;
    try {
      await permissionService.setUserActive(organizationId, profileId, true);
      showAlert("User reactivated", "success", 3000);
      await loadUsers(organizationId);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to reactivate user";
      showAlert(message, "error", 4000);
    } finally {
      organizationUsersStore.isSaving = false;
    }
  }

  async function createUser(payload: {
    email: string;
    firstName: string;
    lastName: string;
    roleIds: number[];
    sendInvite: boolean;
  }): Promise<boolean> {
    organizationUsersStore.isSaving = true;
    try {
      const created = await permissionService.createUserInOrg(organizationId, {
        email: payload.email,
        first_name: payload.firstName,
        last_name: payload.lastName,
        role_ids: payload.roleIds,
        send_invitation_email: payload.sendInvite,
      });
      organizationUsersStore.users = [...organizationUsersStore.users, created];
      showAlert("User created successfully", "success", 3000);
      return true;
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to create user";
      showAlert(message, "error", 4000);
      return false;
    } finally {
      organizationUsersStore.isSaving = false;
    }
  }

  async function updateRoles(profileId: number, roleIds: number[]) {
    organizationUsersStore.isSaving = true;
    try {
      await permissionService.updateProfileRoles(profileId, roleIds);
      showAlert("Roles updated successfully", "success", 3000);
      await loadUsers(organizationId);
    } catch {
      showAlert("Failed to update roles", "error", 3000);
    } finally {
      organizationUsersStore.isSaving = false;
    }
  }

  return {
    users: snap.users,
    roles: snap.roles,
    isLoading: snap.isLoading,
    hasLoaded: snap.hasLoaded,
    isSaving: snap.isSaving,
    error: snap.error,
    activateUser,
    addUser,
    createUser,
    removeUser,
    repairOrg,
    updateRoles,
  };
};

export default useOrganizationUsers;
